import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { createWorkflowNodeExpression, updateTaskNodeExpression } from "../services/expressionBuilder.service";
import { evaluateExpression, generateId, transformWorkflowVariables, transformVariablesToFields } from "../utils/expressionBuilder.utils";

/**
 * Custom hook for expression builder functionality
 * @param {Array} customWorkflowVariables - Workflow variables from API
 * @param {Function} onClose - Close handler
 * @param {string} nodeId - Identifier used in the workflow/task save payload
 * @param {Object} nodeExpression - Existing expression to hydrate
 * @param {Array} userChips - User chip definitions
 * @param {Array} [adminCustomVariables] - Admin variables for linking
 * @param {Object} [builderOptions]
 * @param {Function} [builderOptions.customSaveExpression] - When set, saves via this callback instead of workflow node expression APIs
 * @returns {Object} Expression builder state and handlers
 * @author Abhishek Dhillon
 */
export const useExpressionBuilder = (
  customWorkflowVariables,
  onClose,
  nodeId,
  nodeExpression,
  userChips,
  adminCustomVariables = [],
  builderOptions = {}
) => {
  const { customSaveExpression } = builderOptions;
  const { taskId } = useSelector((state) => state.taskSidebar.task);
  const isTaskSwitchNode = taskId;
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { priorityList = []} = defaultDetails;
  // Expression state
  const [expression, setExpression] = useState(() => {
    return (
      nodeExpression || {
        id: generateId(),
        type: "GROUP",
        operation: "AND",
        children: [
          {
            id: generateId(),
            type: "GROUP",
            operation: "AND",
            children: [
              {
                id: generateId(),
                type: "RULE",
                field: "",
                operator: "",
                value: "",
              },
            ],
          },
        ],
      }
    );
  });

  // Form validation state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Update expression when nodeExpression prop changes
  useEffect(() => {
    if (nodeExpression) {
      setExpression(nodeExpression);
    }
  }, [nodeExpression]);

  /**
   * Updates the root expression
   * @param {Object} newExpression - New expression configuration
   * @author Abhishek Dhillon
   */
  const updateRootExpression = useCallback((newExpression) => {
    setExpression((oldExpression) => ({ ...newExpression }));
  }, []);

  /**
   * Test data for expression evaluation
   */
  const testData = useMemo(
    () => ({
      // Sample test object - replace with actual data structure
      id: "1",
      status: "Active",
      "68466f0f7000ddce6d69f44a": "Gurugram",
      "69317b2bc88b642d85ec9974": 100,
    }),
    []
  );

  // Transform customWorkflowVariables to match expected format
  const availableFields = useMemo(() => {
    return transformWorkflowVariables(customWorkflowVariables, userChips, adminCustomVariables, priorityList);
  }, [customWorkflowVariables, userChips, priorityList]);

  const showIfFields = useMemo(() => {
    return transformVariablesToFields(customWorkflowVariables, userChips, adminCustomVariables);
  }, [ customWorkflowVariables, userChips, adminCustomVariables ]);

  /**
   * Validates the expression form
   * @param {Object} formData - Form data to validate
   * @returns {Object} Validation errors object
   * @author Abhishek Dhillon
   */
  const validateExpressionForm = useCallback((formData) => {
    const errors = {};

    // Check if expression has at least one rule
    if (!formData.expression?.children?.length) {
      errors.expression = "At least one rule is required";
    }

    // Validate each rule in the expression
    const validateRule = (rule, path = "") => {
      if (rule.type === "RULE") {
        if (!rule.field) {
          errors[`${path}field`] = "Field selection is required";
        }
        if (!rule.operator) {
          errors[`${path}operator`] = "Operator selection is required";
        }
        // Check if operator is a standard function that doesn't require a value
        const operatorId = rule?.operator?.id || rule?.operator;
        const isStandardFunction = ["IS_BLANK", "IS_NOT_BLANK", "IS_ZERO", "IS_NOT_ZERO"].includes(operatorId);

        // Skip value validation for standard functions
        if (!isStandardFunction && (rule.value === "" || rule.value === null || rule.value === undefined)) {
          errors[`${path}value`] = "Value is required";
        }
      } else if (rule.type === "GROUP" && rule.children) {
        rule.children.forEach((child, index) => {
          validateRule(child, `${path}children[${index}].`);
        });
      }
    };

    if (formData.expression?.children) {
      formData.expression.children.forEach((child, index) => {
        validateRule(child, `children[${index}].`);
      });
    }

    return errors;
  }, []);

  /**
   * Handles saving the expression
   * @param {Function} onSave - Optional save callback
   * @author Abhishek Dhillon
   */
  const handleSaveExpression = useCallback(
    async () => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const validationErrors = validateExpressionForm({ expression });

        if (Object.keys(validationErrors).length > 0) {
          setSubmitError("Please fix validation errors before submitting");
          setIsSubmitting(false);
          return { errors: validationErrors };
        }

        if (typeof customSaveExpression === "function") {
          const result = await customSaveExpression(expression);
          setIsSubmitting(false);
          if (result?.success) {
            return { success: true };
          }
          setSubmitError(result?.errorMessage || result?.errors?.submit || "Failed to save expression");
          return { errors: result?.errors };
        }

        const submissionData = {
          expression: expression,
          id: nodeId,
        };

        const response = isTaskSwitchNode ? await updateTaskNodeExpression(submissionData) : await createWorkflowNodeExpression(submissionData);
        if (response?.success) {
          setIsSubmitting(false);
          return { success: true };
        }
        setSubmitError(response?.errors?.submit);
        setIsSubmitting(false);
        return { errors: { submit: response?.errors?.submit } };
      } catch (error) {
        setSubmitError(error.message || "Failed to save expression");
        setIsSubmitting(false);
        return { errors: { submit: error.message } };
      }
    },
    [expression, validateExpressionForm, customSaveExpression, nodeId, isTaskSwitchNode]
  );

  /**
   * Handles testing the expression manually
   * @author Abhishek Dhillon
   */
  const handleTestExpression = useCallback(() => {
    if (!expression?.children?.length) {
      return null;
    }

    const result = evaluateExpression(testData, expression);
    // The result is already computed in evaluationResult, this is just for manual trigger
    return result;
  }, [expression, testData]);

  /**
   * Evaluates the current expression against test data
   */
  const evaluationResult = useMemo(() => {
    if (!expression?.children?.length) return null;
    return evaluateExpression(testData, expression);
  }, [expression, testData]);

  /**
   * Form field validators for individual components
   */
  const fieldValidators = useMemo(
    () => ({
      field: (value) => {
        // Check if value exists and has an id property (for field objects)
        if (!value || (typeof value === "object" && !value.id)) {
          return "Field selection is required";
        }
        return "";
      },
      operator: (value) => {
        // Check for empty string, null, or undefined
        if (!value || value === "") {
          return "Operator selection is required";
        }
        return "";
      },
      value: (value, rule) => {
        // Check if operator is a standard function
        const operatorId = rule?.operator?.id || rule?.operator;
        const isStandardFunction = ["IS_BLANK", "IS_NOT_BLANK", "IS_ZERO", "IS_NOT_ZERO"].includes(operatorId);

        // Skip validation for standard functions
        if (isStandardFunction) {
          return "";
        }

        // Check for empty values but allow 0 and false as valid
        if (value === "" || value === null || value === undefined) {
          return "Value is required";
        }
        return "";
      },
    }),
    []
  );

  return {
    expression,
    updateRootExpression,
    handleSaveExpression,
    handleTestExpression,
    testData,
    evaluationResult,
    availableFields,
    showIfFields,
    // Form validation
    validateExpressionForm,
    fieldValidators,
    isSubmitting,
    submitError,
  };
};
